import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Users,
  GraduationCap,
  BookOpen,
  FileText,
  TrendingUp,
  ArrowUpRight,
  ChevronRight,
  Target,
  Layers,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_COLORS = {
  "Consulting": "#2C3E50",
  "Product Management": "#5B7065",
  "Finance / PE / VC": "#8B6F47",
  "Marketing": "#7D5A5A",
  "Sales": "#4A6FA5",
  "Data": "#6B8E8E",
  "HR": "#856088",
  "Founder's Office / Chief of Staff": "#C49A3C",
  "Operations & Supply Chain": "#5C7A5C",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    students_count: 0,
    mentors_count: 0,
    courses_count: 0,
    reports_generated: 0,
    domain_distribution: {},
    secondary_domain_distribution: {},
    career_domains: [],
  });
  const [loading, setLoading] = useState(true);
  const [domainModal, setDomainModal] = useState({ open: false, domain: "", students: [] });
  const [domainFilter, setDomainFilter] = useState("all");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDomainClick = async (domain) => {
    try {
      const response = await axios.get(`${API}/students/by-domain`, {
        params: { domain }
      });
      setDomainModal({
        open: true,
        domain,
        students: response.data.students || [],
        primaryCount: response.data.primary_count || 0,
        secondaryCount: response.data.secondary_count || 0,
      });
      setDomainFilter("all");
    } catch (error) {
      console.error("Failed to fetch domain students:", error);
    }
  };

  const filteredModalStudents = domainModal.students.filter(s => {
    if (domainFilter === "all") return true;
    if (domainFilter === "primary") return s.is_primary;
    return !s.is_primary;
  });

  const domainChartData = Object.entries(stats.domain_distribution || {}).map(
    ([name, value]) => ({
      name: name.length > 15 ? name.substring(0, 15) + "..." : name,
      fullName: name,
      value,
      color: DOMAIN_COLORS[name] || "#D97706",
    })
  );

  const statCards = [
    {
      title: "Total Students",
      value: stats.students_count,
      icon: GraduationCap,
      color: "text-[#B8860B]",
      bgColor: "bg-[#E8B923]/10",
    },
    {
      title: "Mentors Available",
      value: stats.mentors_count,
      icon: Users,
      color: "text-gray-700",
      bgColor: "bg-gray-100",
    },
    {
      title: "Courses Catalogued",
      value: stats.courses_count,
      icon: BookOpen,
      color: "text-gray-600",
      bgColor: "bg-gray-50",
    },
    {
      title: "Reports Generated",
      value: stats.reports_generated,
      icon: FileText,
      color: "text-gray-800",
      bgColor: "bg-gray-100",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-manrope text-3xl font-bold text-gray-900">
            Dashboard
          </h1>
          <p className="text-gray-500 mt-1">
            Pre-MBA Career Analysis Overview
          </p>
        </div>
        <Button
          className="bg-[#E8B923] text-black hover:bg-[#D4A017] font-semibold"
          onClick={() => navigate("/upload")}
          data-testid="upload-data-btn"
        >
          <TrendingUp className="h-4 w-4 mr-2" />
          Upload Data
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card
            key={index}
            className="bg-white border border-gray-200/80 hover:border-gray-300 transition-all duration-300 overflow-hidden relative"
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
            data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#C49A3C] to-[#E8B923]" />
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium tracking-wide uppercase">{stat.title}</p>
                  <p className="font-manrope text-3xl font-bold text-gray-900 mt-1">
                    {stat.value.toLocaleString()}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Domain Distribution Chart */}
        <Card className="lg:col-span-7 bg-white border border-gray-200/80 overflow-hidden relative" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#C49A3C] to-[#E8B923]" />
          <CardHeader>
            <CardTitle className="font-manrope text-xl text-gray-900">
              Domain Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {domainChartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={domainChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      style={{ cursor: 'pointer' }}
                      onClick={(data) => handleDomainClick(data.fullName)}
                    >
                      {domainChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
                      }}
                      formatter={(value, name, props) => [
                        value,
                        props.payload.fullName,
                      ]}
                    />
                    <Legend
                      formatter={(value) => (
                        <span className="text-gray-600 text-sm">{value}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No reports generated yet</p>
                  <p className="text-sm mt-1">
                    Upload student data and generate reports
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Career Domains */}
        <Card className="lg:col-span-5 bg-white border border-gray-200/80 overflow-hidden relative" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#C49A3C] to-[#E8B923]" />
          <CardHeader>
            <CardTitle className="font-manrope text-xl text-gray-900 flex items-center gap-2">
              Career Domains
              <span className="text-xs text-gray-400 font-normal">(Click to view students)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats.career_domains.slice(0, 9).map((domain, index) => {
              const primaryCount = stats.domain_distribution?.[domain] || 0;
              const secondaryCount = stats.secondary_domain_distribution?.[domain] || 0;
              const domainColor = DOMAIN_COLORS[domain] || "#D97706";
              
              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100 hover:border-[#E8B923]/30 transition-all cursor-pointer hover:shadow-sm"
                  onClick={() => handleDomainClick(domain)}
                  data-testid={`domain-card-${domain.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <span className="text-gray-700 text-sm">{domain}</span>
                  <div className="flex items-center gap-2">
                    <Badge
                      className="text-xs"
                      style={{
                        backgroundColor: `${domainColor}15`,
                        color: domainColor,
                      }}
                    >
                      <Target className="h-3 w-3 mr-1" />
                      {primaryCount} primary
                    </Badge>
                    <Badge className="text-xs bg-gray-100 text-gray-600">
                      <Layers className="h-3 w-3 mr-1" />
                      {secondaryCount} secondary
                    </Badge>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Domain Students Modal */}
      <Dialog open={domainModal.open} onOpenChange={(open) => setDomainModal({ ...domainModal, open })}>
        <DialogContent className="max-w-4xl max-h-[80vh] bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Target className="h-5 w-5 text-[#E8B923]" />
              {domainModal.domain}
              <Badge className="ml-2 bg-[#E8B923]/10 text-[#B8860B]">
                {domainModal.primaryCount || 0} Primary
              </Badge>
              <Badge className="bg-gray-100 text-gray-600">
                {domainModal.secondaryCount || 0} Secondary
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={domainFilter} onValueChange={setDomainFilter} className="mt-4">
            <TabsList className="bg-gray-50 border border-gray-200">
              <TabsTrigger value="all" className="data-[state=active]:bg-[#E8B923] data-[state=active]:text-black">
                All ({domainModal.students?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="primary" className="data-[state=active]:bg-[#E8B923] data-[state=active]:text-black">
                Primary ({domainModal.primaryCount || 0})
              </TabsTrigger>
              <TabsTrigger value="secondary" className="data-[state=active]:bg-[#E8B923] data-[state=active]:text-black">
                Secondary ({domainModal.secondaryCount || 0})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="mt-4 max-h-[50vh] overflow-auto">
            {filteredModalStudents.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No students found for this domain
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-200">
                    <TableHead className="text-gray-500">Type</TableHead>
                    <TableHead className="text-gray-500">Name / Email</TableHead>
                    <TableHead className="text-gray-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredModalStudents.map((student, i) => (
                    <TableRow key={i} className="border-gray-200">
                      <TableCell>
                        <Badge className={student.is_primary ? "bg-[#E8B923]/10 text-[#B8860B]" : "bg-gray-100 text-gray-600"}>
                          {student.is_primary ? "Primary" : "Secondary"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{student.name}</p>
                          <p className="text-sm text-gray-500">{student.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          className="bg-[#E8B923] text-black hover:bg-[#D4A017]"
                          onClick={() => {
                            setDomainModal({ ...domainModal, open: false });
                            navigate(`/students/${student.id}`);
                          }}
                        >
                          View Report
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          className="bg-white border border-gray-200/80 hover:border-[#C49A3C]/40 cursor-pointer transition-all group overflow-hidden relative"
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
          onClick={() => navigate("/students")}
          data-testid="quick-action-students"
        >
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gray-200 group-hover:bg-gradient-to-r group-hover:from-[#C49A3C] group-hover:to-[#E8B923] transition-all" />
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">View All</p>
                <p className="font-manrope text-lg font-semibold text-gray-900 mt-1">
                  Student Directory
                </p>
              </div>
              <ArrowUpRight className="h-5 w-5 text-gray-400 group-hover:text-[#B8860B] transition-colors" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="bg-white border border-gray-200/80 hover:border-[#C49A3C]/40 cursor-pointer transition-all group overflow-hidden relative"
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
          onClick={() => navigate("/mentors")}
          data-testid="quick-action-mentors"
        >
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gray-200 group-hover:bg-gradient-to-r group-hover:from-[#C49A3C] group-hover:to-[#E8B923] transition-all" />
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Browse</p>
                <p className="font-manrope text-lg font-semibold text-gray-900 mt-1">
                  Mentor Network
                </p>
              </div>
              <ArrowUpRight className="h-5 w-5 text-gray-400 group-hover:text-[#B8860B] transition-colors" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="bg-white border border-gray-200/80 hover:border-[#C49A3C]/40 cursor-pointer transition-all group overflow-hidden relative"
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
          onClick={() => navigate("/resources")}
          data-testid="quick-action-resources"
        >
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gray-200 group-hover:bg-gradient-to-r group-hover:from-[#C49A3C] group-hover:to-[#E8B923] transition-all" />
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Manage</p>
                <p className="font-manrope text-lg font-semibold text-gray-900 mt-1">
                  Resources Library
                </p>
              </div>
              <ArrowUpRight className="h-5 w-5 text-gray-400 group-hover:text-[#B8860B] transition-colors" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
